import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { ROUTES } from "@/routes";
import { StatusBadge } from "@/presentation/provisioning/components/StatusBadge";
import {
  STEP_LABELS,
  type ProvisionStep,
} from "@/application/provisioning/types";
import { Loader2 } from "lucide-react";

export function TerminalAnalyticsView() {
  const { dashboardBloc } = useAdminApp();
  const [state] = useStateMachineValue(dashboardBloc);
  const navigate = useNavigate();

  useEffect(() => {
    dashboardBloc.load();
  }, [dashboardBloc]);

  if (state.loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-foreground mb-6 text-2xl font-bold">
        Terminal Analytics
      </h1>

      <div className="mb-8 grid grid-cols-4 gap-4">
        <StatCard label="Today" value={state.todayCount} />
        <StatCard label="This Week" value={state.weekCount} />
        <StatCard
          label="Needs Attention"
          value={state.failedCount}
          variant={state.failedCount > 0 ? "danger" : "default"}
        />
        <StatCard label="Total Accounts" value={state.totalCount} />
      </div>

      {state.failedItems.length > 0 && (
        <div className="bg-card mb-6 overflow-hidden rounded-lg border">
          <div className="border-b px-4 py-3">
            <h2 className="text-foreground text-sm font-semibold">
              ⚠ Needs Attention
            </h2>
          </div>
          {state.failedItems.map((item) => (
            <div
              key={item.id}
              className="border-border hover:bg-muted flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-0"
              onClick={() =>
                navigate(ROUTES.ACCOUNT_DETAIL.replace(":id", item.id))
              }
            >
              <StatusBadge status={item.status} />
              <span className="text-foreground font-medium">
                {item.businessName}
              </span>
              <span className="text-muted-foreground text-xs">
                ·{" "}
                {STEP_LABELS[item.failedStep as ProvisionStep] ??
                  item.failedStep}
              </span>
              <span className="text-subtle-foreground ml-auto text-xs">
                {timeAgo(item.updatedAt)}
              </span>
              <span className="text-xs text-blue-600">View →</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-card overflow-hidden rounded-lg border">
        <div className="border-b px-4 py-3">
          <h2 className="text-foreground text-sm font-semibold">
            Recent Activity
          </h2>
        </div>
        {state.recentItems.length === 0 ? (
          <div className="text-subtle-foreground px-4 py-8 text-center text-sm">
            No provisioning activity yet
          </div>
        ) : (
          state.recentItems.map((item) => (
            <div
              key={item.id}
              className="border-border hover:bg-muted flex cursor-pointer items-center gap-3 border-b px-4 py-3 last:border-0"
              onClick={() =>
                navigate(ROUTES.ACCOUNT_DETAIL.replace(":id", item.id))
              }
            >
              <StatusBadge status={item.status} />
              <span className="text-foreground font-medium">
                {item.businessName}
              </span>
              <span className="text-muted-foreground text-xs">
                · {item.email}
              </span>
              <span className="text-subtle-foreground ml-auto text-xs">
                {timeAgo(item.createdAt)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number;
  variant?: "default" | "danger";
}) {
  return (
    <div className="bg-card rounded-lg border p-4">
      <div
        className={`text-xs font-medium tracking-wide uppercase ${
          variant === "danger"
            ? "font-semibold text-red-600"
            : "text-muted-foreground"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-1 text-3xl font-bold ${
          variant === "danger" && value > 0 ? "text-red-600" : "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
